import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Award, Medal, CheckCircle, Coins } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminResultsPage = () => {
  const { tournamentId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [awardEdits, setAwardEdits] = useState({});
  const [awarding, setAwarding] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const openPointsDialog = async () => {
    if (results.length === 0) {
      toast.error('יש לבחור מיקום לפחות לשחקן אחד לפני חלוקת נקודות');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/api/admin/tournaments/${tournamentId}/points-preview`,
        { results: results.map(r => ({ user_id: r.user_id, position: r.position })) },
        authHeaders()
      );
      setPreview(res.data);
      const edits = {};
      res.data.players.forEach(p => {
        edits[p.user_id] = p.previously_awarded > 0 ? p.previously_awarded : p.suggested_points;
      });
      setAwardEdits(edits);
      setShowPointsDialog(true);
    } catch (e) {
      toast.error('שגיאה בחישוב הנקודות');
    }
  };

  const handleAwardPoints = async () => {
    if (!preview) return;
    setAwarding(true);
    try {
      const awards = preview.players.map(p => ({
        user_id: p.user_id,
        points: parseInt(awardEdits[p.user_id], 10) || 0,
      }));
      const res = await axios.post(
        `${API}/api/admin/tournaments/${tournamentId}/award-points`,
        { awards },
        authHeaders()
      );
      toast.success(`נקודות חולקו ל-${res.data.players_updated} שחקנים! 🎉`);
      setShowPointsDialog(false);
    } catch (e) {
      toast.error('שגיאה בחלוקת הנקודות');
    } finally {
      setAwarding(false);
    }
  };

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('אין לך הרשאות אדמין');
      navigate('/');
      return;
    }
    fetchTournamentData();
  }, [user, navigate, tournamentId]);

  const fetchTournamentData = async () => {
    try {
      // קבלת פרטי תחרות
      const tournamentResponse = await axios.get(`${API}/api/tournaments/${tournamentId}`);
      setTournament(tournamentResponse.data);

      // קבלת פרטי שחקנים רשומים
      const playerIds = tournamentResponse.data.registered_players || [];
      if (playerIds.length > 0) {
        const playersData = await Promise.all(
          playerIds.map(async (playerId) => {
            try {
              const response = await axios.get(`${API}/api/users/${playerId}`);
              return response.data;
            } catch {
              return null;
            }
          })
        );
        setPlayers(playersData.filter(p => p !== null));
      }
    } catch (error) {
      toast.error('שגיאה בטעינת נתוני תחרות');
    } finally {
      setLoading(false);
    }
  };

  const handleSetResult = (playerId, position) => {
    // בדוק אם השחקן כבר קיים ברשימה
    const existingIndex = results.findIndex(r => r.user_id === playerId);
    
    if (existingIndex >= 0) {
      // עדכן מיקום קיים
      const newResults = [...results];
      newResults[existingIndex] = { user_id: playerId, position, points_earned: 0 };
      setResults(newResults);
    } else {
      // הוסף חדש
      setResults([...results, { user_id: playerId, position, points_earned: 0 }]);
    }
  };

  const handleSubmitResults = async () => {
    if (results.length === 0) {
      toast.error('יש לבחור לפחות תוצאה אחת');
      return;
    }

    if (!window.confirm('האם אתה בטוח? פעולה זו תעדכן את הנקודות של השחקנים ותסיים את התחרות.')) {
      return;
    }

    try {
      await axios.post(`${API}/api/admin/tournaments/${tournamentId}/complete`, results);
      toast.success('תוצאות עודכנו בהצלחה! 🎉');
      navigate('/admin/tournaments');
    } catch (error) {
      toast.error('שגיאה בעדכון תוצאות');
    }
  };

  const getPositionLabel = (position) => {
    const isDaily = tournament?.type === 'daily';
    const labels = {
      '1st': { text: 'מקום 1 🥇', points: isDaily ? 150 : 350, color: 'bg-yellow-100 text-yellow-800' },
      '2nd': { text: 'מקום 2 🥈', points: isDaily ? 90 : 250, color: 'bg-gray-100 text-gray-800' },
      '3rd': { text: 'מקום 3 🥉', points: 170, color: 'bg-orange-100 text-orange-800' },
      'semi_final': { text: 'חצי גמר', points: isDaily ? 65 : 120, color: 'bg-blue-100 text-blue-800' },
      'quarter_final': { text: 'רבע גמר', points: isDaily ? 35 : 80, color: 'bg-green-100 text-green-800' },
      'round_of_16': { text: 'שמינית גמר', points: 50, color: 'bg-purple-100 text-purple-800' },
      'round_of_32': { text: 'סיבוב ראשון', points: 25, color: 'bg-pink-100 text-pink-800' },
      'group_stage': { text: 'שלב הבתים', points: isDaily ? 15 : 0, color: 'bg-gray-50 text-gray-600' }
    };
    return labels[position] || { text: position, points: 0, color: 'bg-gray-100 text-gray-800' };
  };

  const getPlayerResult = (playerId) => {
    return results.find(r => r.user_id === playerId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">טוען...</div>
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">תחרות לא נמצאה</div>
        </div>
      </div>
    );
  }

  const positions = tournament.type === 'daily'
    ? ['1st', '2nd', 'semi_final', 'quarter_final', 'group_stage']
    : ['1st', '2nd', '3rd', 'semi_final', 'quarter_final', 'round_of_16', 'round_of_32'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🏆 עדכון תוצאות
          </h1>
          <p className="text-xl text-gray-600">{tournament.name}</p>
          <p className="text-sm text-gray-500">{tournament.date} | {tournament.location}</p>
        </div>

        {/* Players List */}
        <Card className="mb-8 glass-effect">
          <CardHeader>
            <CardTitle>שחקנים רשומים ({players.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {players.length === 0 ? (
              <p className="text-gray-500 text-center py-8">אין שחקנים רשומים</p>
            ) : (
              <div className="space-y-4">
                {players.map((player) => {
                  const playerResult = getPlayerResult(player.id);
                  
                  return (
                    <div key={player.id} className="border rounded-lg p-4 hover:bg-blue-50 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{player.full_name}</h3>
                          <p className="text-sm text-gray-600">{player.skill_level}</p>
                        </div>
                        {playerResult && (
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getPositionLabel(playerResult.position).color}`}>
                            {getPositionLabel(playerResult.position).text} - {getPositionLabel(playerResult.position).points} נקודות
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {positions.map((position) => {
                          const posInfo = getPositionLabel(position);
                          const isSelected = playerResult?.position === position;
                          
                          return (
                            <Button
                              key={position}
                              size="sm"
                              variant={isSelected ? "default" : "outline"}
                              onClick={() => handleSetResult(player.id, position)}
                              className={isSelected ? "bg-blue-600" : ""}
                            >
                              {posInfo.text}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {results.length > 0 && (
          <Card className="mb-8 glass-effect border-2 border-green-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                סיכום תוצאות
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                {results.map((result) => {
                  const player = players.find(p => p.id === result.user_id);
                  const posInfo = getPositionLabel(result.position);
                  
                  return (
                    <div key={result.user_id} className="flex justify-between items-center p-3 bg-white rounded-lg">
                      <span className="font-medium">{player?.full_name}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${posInfo.color}`}>
                        {posInfo.text} - {posInfo.points} נקודות
                      </span>
                    </div>
                  );
                })}
              </div>

              <Button 
                className="w-full btn-primary text-lg py-6"
                onClick={handleSubmitResults}
                data-testid="save-results-btn"
              >
                <Trophy className="w-5 h-5 ml-2" />
                שמור תוצאות וסיים תחרות
              </Button>

              <Button
                className="w-full mt-3 text-lg py-6 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={openPointsDialog}
                data-testid="distribute-points-btn"
              >
                <Coins className="w-5 h-5 ml-2" />
                חשב וחלק נקודות אוטומטית לפי דירוג
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-4">
          <Button 
            variant="outline"
            onClick={() => navigate('/admin/tournaments')}
          >
            חזור
          </Button>
        </div>
      </div>

      {/* Dialog: חלוקת נקודות אוטומטית */}
      <Dialog open={showPointsDialog} onOpenChange={setShowPointsDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl" data-testid="award-points-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-right">
              <Coins className="w-6 h-6 text-amber-500" />
              חלוקת נקודות לפי דירוג ({preview?.tournament_type === 'daily' ? 'יומי' : 'נוקאאוט'})
            </DialogTitle>
            <DialogDescription className="sr-only">חלוקת נקודות אוטומטית לשחקני התחרות לפי המיקום הסופי</DialogDescription>
          </DialogHeader>

          {preview?.points_awarded && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800" data-testid="already-awarded-warning">
              ⚠️ כבר חולקו נקודות לתחרות זו. חלוקה מחדש תבטל את ההענקה הקודמת ותחיל את הערכים החדשים (ללא ספירה כפולה).
            </div>
          )}

          <p className="text-sm text-gray-500 text-right">
            הנקודות חושבו אוטומטית לפי המיקום של כל שחקן. ניתן לערוך לפני אישור.
          </p>

          <div className="space-y-2">
            {preview?.players?.length === 0 && (
              <p className="text-center text-gray-500 py-4">אין שחקנים זכאים לנקודות (שחקנים חיצוניים מדולגים).</p>
            )}
            {preview?.players?.map((p) => (
              <div key={p.user_id} className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`award-row-${p.user_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.name}</div>
                  <div className="text-xs text-gray-500">
                    {p.position_label} · נוכחי: {p.current_total_points} נק׳
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input
                    type="number"
                    min="0"
                    value={awardEdits[p.user_id] ?? 0}
                    onChange={(e) => setAwardEdits(prev => ({ ...prev, [p.user_id]: e.target.value }))}
                    className="w-24 text-center"
                    data-testid={`award-input-${p.user_id}`}
                  />
                  <span className="text-sm text-gray-500">נק׳</span>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowPointsDialog(false)} data-testid="award-cancel-btn">
              ביטול
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleAwardPoints}
              disabled={awarding || !preview?.players?.length}
              data-testid="award-confirm-btn"
            >
              {awarding ? 'מחלק...' : 'אשר וחלק נקודות'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResultsPage;
